import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { Calendar, ArrowLeft, ArrowRight, Tag } from "lucide-react";
import { API } from "../../data/constants";
import { SEO } from "../../components/SEO";
import { formatPostDate, readingTimeFromHtml } from "./blogHelpers";

// Configure DOMPurify once. Allow standard rich-text tags + safe attributes
// produced by Tiptap. Forbid <script>, <iframe>, <object>, <embed>, and any
// inline event handlers so admin-authored HTML can never inject script.
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    "p", "br", "strong", "b", "em", "i", "u", "s", "strike", "code", "pre",
    "h1", "h2", "h3", "h4", "h5", "h6",
    "ul", "ol", "li",
    "blockquote", "hr",
    "a", "img",
    "span", "div"
  ],
  ALLOWED_ATTR: ["href", "src", "alt", "title", "target", "rel", "class"],
  ALLOW_DATA_ATTR: false,
  FORBID_TAGS: ["script", "iframe", "object", "embed", "style", "form", "input"],
  FORBID_ATTR: ["onclick", "onerror", "onload", "onmouseover", "style"]
};

const RelatedCard = ({ post }) => (
  <Link to={`/blog/${post.slug}`} className="bg-white rounded-2xl shadow-card overflow-hidden card-hover block" data-testid={`related-${post.slug}`}>
    {post.cover_image_url && (
      <img src={post.cover_image_url} alt={post.title} className="w-full h-32 object-cover" />
    )}
    <div className="p-4">
      {post.category && <span className="text-xs text-orange-primary font-medium">{post.category}</span>}
      <p className="font-display font-bold text-gray-dark mt-1 line-clamp-2">{post.title}</p>
    </div>
  </Link>
);

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(undefined); // undefined=loading, null=404
  const [related, setRelated] = useState([]);

  const sanitizedHtml = useMemo(
    () => (post && post.content ? DOMPurify.sanitize(post.content, SANITIZE_CONFIG) : ""),
    [post]
  );

  useEffect(() => {
    setPost(undefined);
    axios.get(`${API}/blog/posts/${slug}`)
      .then(({ data }) => setPost(data))
      .catch(() => setPost(null));
    axios.get(`${API}/blog/posts/${slug}/related`)
      .then(({ data }) => setRelated(data))
      .catch(() => setRelated([]));
  }, [slug]);

  if (post === undefined) {
    return <div className="pt-32 min-h-screen text-center text-gray-medium">Loading…</div>;
  }
  if (post === null) {
    return (
      <div className="pt-32 min-h-screen text-center" data-testid="blog-post-404">
        <h1 className="font-display text-3xl text-gray-dark mb-3">Post not found</h1>
        <Link to="/blog" className="text-orange-primary font-semibold inline-flex items-center gap-1">
          <ArrowLeft size={14} /> Back to all posts
        </Link>
      </div>
    );
  }

  return (
    <article className="pt-20 min-h-screen bg-white" data-testid={`blog-post-${post.slug}`}>
      <SEO
        title={post.title}
        description={post.excerpt || post.title}
        path={`/blog/${post.slug}`}
        image={post.cover_image_url || undefined}
      />

      <header className="section-padding bg-gradient-to-br from-orange-light via-white to-white">
        <div className="max-w-3xl mx-auto">
          <Link to="/blog" className="inline-flex items-center gap-1 text-orange-primary text-sm font-semibold mb-6">
            <ArrowLeft size={14} /> All posts
          </Link>
          {post.category && (
            <Link to={`/blog?category=${encodeURIComponent(post.category)}`} className="inline-block bg-orange-primary/10 text-orange-primary text-xs px-3 py-1 rounded-full font-medium mb-4">
              {post.category}
            </Link>
          )}
          <h1 className="font-display text-4xl md:text-5xl font-bold text-gray-dark leading-tight">{post.title}</h1>
          {post.excerpt && <p className="text-lg text-gray-medium mt-4 leading-relaxed">{post.excerpt}</p>}
          <div className="flex items-center gap-3 text-sm text-gray-medium mt-6 flex-wrap">
            <span className="inline-flex items-center gap-1"><Calendar size={13} /> {formatPostDate(post.published_at)}</span>
            <span>· {readingTimeFromHtml(post.content)} min read</span>
            {typeof post.view_count === "number" && <span>· {post.view_count} views</span>}
          </div>
        </div>
      </header>

      {post.cover_image_url && (
        <div className="max-w-4xl mx-auto px-4 -mt-6">
          <img src={post.cover_image_url} alt={post.title} className="w-full h-72 md:h-[400px] object-cover rounded-2xl shadow-card" />
        </div>
      )}

      <section className="section-padding">
        <div
          className="max-w-3xl mx-auto blog-content"
          dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
        />
      </section>

      {(post.author_name || post.author_bio) && (
        <section className="section-padding bg-gray-light pt-0">
          <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-card p-6 flex items-start gap-4" data-testid="blog-author-bio">
            {post.author_avatar_url ? (
              <img src={post.author_avatar_url} alt={post.author_name} className="w-16 h-16 rounded-full object-cover" />
            ) : (
              <div className="w-16 h-16 rounded-full gradient-orange flex items-center justify-center text-white font-bold text-xl flex-shrink-0" aria-hidden="true">
                {(post.author_name || "?").charAt(0)}
              </div>
            )}
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-medium">About the author</p>
              <p className="font-display font-bold text-gray-dark text-lg mt-1">{post.author_name}</p>
              {post.author_bio && <p className="text-sm text-gray-medium mt-1">{post.author_bio}</p>}
            </div>
          </div>
        </section>
      )}

      {post.tags && post.tags.length > 0 && (
        <section className="pb-8">
          <div className="max-w-3xl mx-auto px-4">
            <p className="text-xs uppercase tracking-wider text-gray-medium mb-2 inline-flex items-center gap-1"><Tag size={11} /> Tagged</p>
            <div className="flex flex-wrap gap-2">
              {post.tags.map((t) => (
                <Link
                  key={t}
                  to={`/blog?tag=${encodeURIComponent(t)}`}
                  className="text-xs bg-orange-light text-orange-primary px-3 py-1 rounded-full hover:bg-orange-primary/20"
                >#{t}</Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {related.length > 0 && (
        <section className="section-padding bg-gray-light">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-display text-2xl md:text-3xl font-bold text-gray-dark">Related posts</h2>
              <Link to="/blog" className="text-orange-primary font-semibold text-sm inline-flex items-center gap-1">
                All posts <ArrowRight size={13} />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((p) => <RelatedCard key={p.id} post={p} />)}
            </div>
          </div>
        </section>
      )}
    </article>
  );
};

export default BlogPostPage;
